import React from 'react';

import { StudyExportOperation } from './useStudyExportController';

export type StartExport = (operation: StudyExportOperation) => Promise<boolean>;

export interface SeparateOutput {
  id: string;
  title: string;
  run: (signal: AbortSignal) => Promise<unknown>;
}

export type SeparateOutputStatus = 'running' | 'done' | 'failed' | 'cancelled' | 'not-started';

export interface SeparateOutputOutcome {
  status: SeparateOutputStatus;
  error?: string;
}

const isRetryable = (outcome?: SeparateOutputOutcome) =>
  outcome?.status === 'failed' || outcome?.status === 'cancelled' || outcome?.status === 'not-started';

// Runs each selected exporter in turn through the shared export controller; one failure does not stop the others.
export const useSeparateOutputExports = (startExport: StartExport) => {
  const [outcomes, setOutcomes] = React.useState<Record<string, SeparateOutputOutcome>>({});
  const outcomesRef = React.useRef(outcomes);
  const lastOutputs = React.useRef<SeparateOutput[]>([]);

  const record = React.useCallback((id: string, outcome: SeparateOutputOutcome) => {
    outcomesRef.current = { ...outcomesRef.current, [id]: outcome };
    setOutcomes(outcomesRef.current);
  }, []);

  const runOutputs = React.useCallback(
    async (outputs: SeparateOutput[]) => {
      for (const output of outputs) record(output.id, { status: 'not-started' });
      for (const [index, output] of outputs.entries()) {
        let cancelled = false;
        const started = await startExport({
          title: output.title,
          canCancel: true,
          closeOnSuccess: true,
          run: async (signal) => {
            record(output.id, { status: 'running' });
            try {
              await output.run(signal);
              record(output.id, { status: 'done' });
            } catch (error) {
              cancelled = signal.aborted;
              record(output.id, {
                status: cancelled ? 'cancelled' : 'failed',
                error: error instanceof Error ? error.message : 'Export failed.',
              });
              throw error;
            }
          },
        });
        if (!started) {
          record(output.id, { status: 'failed', error: 'Another export is already running.' });
          for (const remaining of outputs.slice(index + 1)) record(remaining.id, { status: 'not-started' });
          return;
        }
        if (cancelled) return;
      }
    },
    [record, startExport]
  );

  const exportOutputs = React.useCallback(
    (outputs: SeparateOutput[]) => {
      lastOutputs.current = outputs;
      outcomesRef.current = {};
      setOutcomes({});
      return runOutputs(outputs);
    },
    [runOutputs]
  );

  const retryFailedOutputs = React.useCallback(
    () => runOutputs(lastOutputs.current.filter((output) => isRetryable(outcomesRef.current[output.id]))),
    [runOutputs]
  );

  const hasRetryableOutputs = Object.values(outcomes).some(isRetryable);

  return { outcomes, exportOutputs, retryFailedOutputs, hasRetryableOutputs };
};

const statusText: Record<SeparateOutputStatus, string> = {
  running: 'Exporting…',
  done: 'Downloaded',
  failed: 'Failed',
  cancelled: 'Cancelled',
  'not-started': 'Not started',
};

export const describeSeparateOutputOutcome = (outcome: SeparateOutputOutcome) =>
  outcome.error && outcome.status !== 'done'
    ? `${statusText[outcome.status]}: ${outcome.error}`
    : statusText[outcome.status];
